// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { toast } from 'react-toastify';
import { withRouter } from 'react-router-dom';
import ImportFileProvider from './FileProvider';
import '../../../styles/import.css';
import api from '../../../api';

const SCRIPT_TEMPLATE = "# node = {\n#     \"createdOn\": \"date\",\n#     \"dav_uri\": \"path\",\n#     \"link_uri\": \"link to page\",\n#     \"modifiedOn\": \"Data\",\n#     \"name\": \"Name of the item\",\n#     \"nodeType\": \"type of item\",\n#     \"uri\": \"Path\",\n#     \"your_permissions\": \"Number\"\n# }\n\n# View documentation here: https://developer.smartvault.com/api\n\ndef run(node): \n    return True";

class ImportSmartVault extends ImportFileProvider {
    // State from ImportFileProvider 
    // state = {
    //     authenticated, 
    //     configurations,
    //     config, 
    //     edit, 
    //     oauth, 
    //     folders, 
    //     expanded,
    //     stacks,
    //     template,
    // }

    state = {
        ...this.state, // Spreads ImportFileProvider into state... 
        pin: '',
        email: '',
        loading: false, 
    }

    scriptTemplate = SCRIPT_TEMPLATE; 

    loadConfig = async () => {
        const { connectionIdx } = this.props.data; 
        const config = this.props.team.sync?.smartvault[connectionIdx] || {
            pin: '',
            oauth: '',
            email: this.props.user.email,
            configurations: [],
            authenticated: false, 
        };

        this.setState({
            ...config, 
            configurations: config.folders || [],
            scriptTemplate: SCRIPT_TEMPLATE, 
        });
    }

    saveConfig = async () => {

        const sync = this.props.team?.sync?? {}; 
        const connections = sync?.smartvault??[];
        const connectionIdx = this.props.data?.connectionIdx ?? connections.length; 

        const connection = {
            user: this.props.user._id,
            pin: this.state.pin, 
            oauth: this.state.oauth,
            userEmail: this.state.email,   
            ...(this.state.authenticated ? (connections[connectionIdx] || {}) : {}),
            folders: this.state.configurations,
        };

        if(connections.length === 0)
            connections.push(connection);
        else
            connections[connectionIdx] = connection; 

        sync.smartvault = connections;
        await this.props.updateTeam({sync});
    }

    authenticate = async() => {
        // Do we have a pin? If not we need to get it.
        if(!this.state.pin){
            const res = await api.get("/sync/smartvault/pin");
            if(!res.success)
                return toast.error("Error getting SmartVault token");
            return window.open(res.url, "_blank").focus();
        }

        // We have a pin, lets get the oauth connection.  
        const res = await api.get("/sync/smartvault/token?code=" + this.state.pin); 
        if(!res.success)
            return toast.error("Error getting SmartVault token");

        // Does the config already have a oauth set? 
        let { connectionIdx } = this.props.data;
        connectionIdx = connectionIdx || 0; 
        const connections = this.props.team.sync?.smartvault??[];
        // If no connections, we have an issue...
        if(connections.length === 0)
            return toast.error("Error getting SmartVault token");

        const connection = connections[connectionIdx];

        // Do we need to update the oauth? 
        if(!connection.oauth || connection.oauth !== res.oauthId){
            connection.oauth = res.oauthId;
            connections[connectionIdx] = connection;
            const sync = this.props.team.sync?? {}; 
            sync.smartvault = connections;
            await this.props.updateTeam({sync});
        }
        // Set the oauth id in state... 
        this.setState({oauth: res.oauthId, authenticated: true});
    }

    loadRoot = async() => {
        this.setState({loading: true});
        const root = await this.listFolders("");
        this.setState({folders: root?.folders??[], loading: false});
    }

    listFolders = async(path) => {
        if(!this.state.authenticated)
            return; 
        
        let hasMore = true; 
        let limit = 100;
        let page = 0; 
        const items = []; 
        while(hasMore){
            const res = await api.post("/sync/smartvault/list", {
                oauth: this.state.oauth, 
                path, 
                children: 0, 
                page: page, 
                perPage: limit,
            });

            if(!res.success){
                toast.error("Error getting SmartVault folders");
                break; 
            }

            if(!res.items.children)
                break; 

            items.push(...res?.items?.children);
            const {total_containers, total_folders, total_vaults} = res.items;
            hasMore = items.length < total_vaults + total_containers + total_folders;
            page += 1;
        }

        const folders = items.map((item) => {
            return {
                name: item.name, 
                displayName: item.name,
                path: this.removeFirstSlash(`${path}/${item.name}`),
                _id: `${item.createdOn}_${item.name}`,
                loadChildren: async() => {
                    const children = await this.listFolders(`${path}/${item.name}`);
                    this.setState({folders: [...this.state.folders, ...children.folders]});
                    this.loaded = true; 
                }
            }
        });

        return {folders}; 
    }

    renderContent = () => {
        return (
            <>
                {!this.state.authenticated && <>

                    <div
                        className='modal-content modal-center'
                    >
                        {this.renderDescription()}
                        <p>
                            To get started, please enter your SmartVault credentials below.
                        </p>
                        <br />
                    
                        <h2>Configure connection settings</h2>
                        <form
                            className="import-config-form"
                            onSubmit={this.saveConfig}
                        >
                            <div
                                className='config'
                            >
                                <label>Email</label>
                                <input
                                    type={"text"}
                                    className="config-field-string"
                                    placeholder="Enter your PensionPro username or email..."
                                    value={this.state.email}
                                    onChange={e => this.setState({ email: e.target.value })}    
                                />
                                <div
                                    className='hint'
                                    style={{
                                        paddingTop: 4
                                    }}
                                >
                                    <p>
                                        What email address do you use to login to your SmartVault account?
                                    </p>
                                </div>
                            </div>
                    
                            <div
                                className='config'
                            >
                                <label>Pin</label>
                                <input
                                    type={"text"}
                                    className="config-field-string"
                                    placeholder="Enter your PensionPro Firm API key..."
                                    value={this.state.pin}
                                    onChange={e => this.setState({ pin: e.target.value })}    
                                />
                                <div
                                    className='hint'
                                    style={{
                                        paddingTop: 4
                                    }}
                                >
                                    <p>
                                        This is the pin you received from SmartVault when you authorized Stax·ai to access your SmartVault account.
                                    </p>
                                </div>
                            </div>


                            <input
                                className="import-config-button"
                                value="Connect"
                                type='submit'
                                onClick={async(e) => {
                                    e.preventDefault(); 
                                    e.stopPropagation(); 
                                    // Save the team with the update config... 
                                    await this.saveConfig();
                                    await this.authenticate(); 
                                }}
                            />
                            <div style={{ clear: 'both' }} />
                        </form>
                    </div>
                </>}
                {
                    this.state.authenticated && <>{this.renderConfigEditor()}</>
                }
            </>
        )
    }

    renderDescription = () => {
        return(
            <div>
                <h1>Sync documents from SmartVault</h1>

                <p>
                    Stax·ai will make stacks for each SmartVault folder you select below and mirror the contents of these folders into your new stacks.
                </p><br />
            </div>
        )
    }

    renderInputPin = () => {
        return(
        <>
            <div>
                <label>Pin</label>
                <input
                    className='config-field-string'
                    type='text'
                    value={this.state.pin}
                    onChange={(e)=> this.setState({pin: e.target.value})}
                />
            </div>
            <br />
        </>
        )
    }
    
}

export default withRouter(ImportSmartVault);
